import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import {
  ALLOWED_DOCUMENT_MIME_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
} from "@shared/finance/supplierInvoices";
import { Download, Eye, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

interface Doc {
  id: number;
  fileName: string;
  mimeType?: string | null;
  fileSize?: number | null;
  createdAt: string;
  deletedAt?: string | null;
}

interface SupplierInvoiceDocumentsProps {
  invoiceId: number;
  documents: Doc[];
  canWrite: boolean;
}

const ALLOWED_MIME_SET = new Set<string>(ALLOWED_DOCUMENT_MIME_TYPES);

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function safeFileName(name: string): string {
  // Strip diacritics: NFD normalize then remove combining marks
  const nfd = name.normalize("NFD").replace(/[̀-ͯ]/g, "");
  // Replace anything not allowed by backend with underscore
  return nfd.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 255);
}

export async function uploadSupplierInvoiceDocumentFile(invoiceId: number, file: File): Promise<{ id: number }> {
  const API_URL = (import.meta as { env: Record<string, string> }).env.VITE_API_URL || "";
  const response = await fetch(
    `${API_URL}/api/finance/supplier-invoices/${invoiceId}/documents`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": file.type,
        "X-File-Name": safeFileName(file.name),
      },
      body: file,
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Error ${response.status}: ${text}`);
  }
  return response.json() as Promise<{ id: number }>;
}

export function SupplierInvoiceDocuments({ invoiceId, documents, canWrite }: SupplierInvoiceDocumentsProps) {
  const utils = trpc.useUtils();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const deleteDocument = trpc.supplierInvoices.deleteDocument.useMutation({
    onSuccess: () => {
      toast.success("Documento eliminado");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      utils.supplierInvoices.listDocuments.invalidate({ invoiceId });
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_MIME_SET.has(file.type)) {
      toast.error("Tipo de archivo no permitido. Solo PDF, JPG, PNG.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      toast.error("El archivo supera el límite de 20MB.");
      e.target.value = "";
      return;
    }
    setPendingFile(file);
  }

  async function handleUpload() {
    if (!pendingFile) return;
    setUploading(true);
    try {
      await uploadSupplierInvoiceDocumentFile(invoiceId, pendingFile);
      toast.success("Documento subido correctamente");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      utils.supplierInvoices.listDocuments.invalidate({ invoiceId });
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al subir el documento";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  }

  const API_URL = (import.meta as { env: Record<string, string> }).env.VITE_API_URL || "";
  const active = documents.filter((d) => !d.deletedAt);

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
          <p className="text-sm font-medium">Subir documento</p>
          <p className="text-xs text-muted-foreground">PDF, JPG o PNG — máx. 20MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
          />
          {pendingFile && (
            <div className="flex items-center justify-between bg-background rounded p-2 border text-sm">
              <span className="truncate text-foreground">
                {pendingFile.name} ({formatBytes(pendingFile.size)})
              </span>
              <Button size="sm" onClick={handleUpload} disabled={uploading}>
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Subiendo...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-1" />Subir</>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium">
          {active.length} {active.length === 1 ? "documento" : "documentos"}
        </p>
        {active.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Sin documentos adjuntos.</p>
        ) : (
          <div className="divide-y border rounded-lg">
            {active.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 hover:bg-muted/20 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{doc.fileName}</p>
                    {doc.fileSize && (
                      <p className="text-xs text-muted-foreground">{formatBytes(doc.fileSize)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`${API_URL}/api/finance/supplier-invoices/documents/${doc.id}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-ods-navigation hover:underline"
                  >
                    <Eye className="h-4 w-4" />
                    Ver
                  </a>
                  <a
                    href={`${API_URL}/api/finance/supplier-invoices/documents/${doc.id}/download`}
                    download={doc.fileName}
                    className="inline-flex items-center gap-1 text-sm text-ods-navigation hover:underline"
                  >
                    <Download className="h-4 w-4" />
                    Descargar
                  </a>
                  {canWrite && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-destructive h-8 w-8"
                      onClick={() => deleteDocument.mutate({ documentId: doc.id })}
                      disabled={deleteDocument.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
